import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import useWindowSize from 'hooks/useWindowSize';
import timedFetch from 'middleware/timedFetch';
import { trackError } from 'helpers/ErrorUtils';
import type { RequestSponsoredAdsBody, RequestSponsoredAdsBodyFilters, SponsoredAdsResponse, SponsoredAdsResults } from 'apis/sponsoredAds';
import useMartyContext from 'hooks/useMartyContext';
import { AD_PLACEMENT_COUNTS } from 'constants/sponsoredAds';
import { selectSponsoredAdsConfig } from 'selectors/environment';
import type { AppState } from 'types/app';
import { getViewportAsString } from 'utils/viewportUtil';
import { getAmazonSponsoredAds } from 'apis/amazonAds';
import type { AmazonAdsResponse } from 'apis/amazonAds';

export const trackSponsoredAdImpression = (
  { url, fetcherName = 'SponsoredAdImpressions' }: { url: string; fetcherName?: string },
  fetcher = timedFetch
) => {
  const fetch = fetcher(fetcherName);
  fetch(url).catch((error: any) => trackError('ERROR', 'Failed to send Amazon Ads impression event', error));
};

export const trackSponsoredAdView = ({ url, fetcherName = 'SponsoredAdViews' }: { url: string; fetcherName?: string }, fetcher = timedFetch) => {
  const fetch = fetcher(fetcherName);
  fetch(url).catch((error: any) => trackError('ERROR', 'Failed to send Amazon Ads view event', error));
};

function concatIfExists(keyword = '', val?: any) {
  if (val) {
    //purposeful whitespace.
    return `${keyword} ${val}`;
  }
  return keyword;
}

// builds the keyword for search implementation based off filters.
// keyword should be "gender + most specific zc + brand[0] + color[0]"
export function buildKeywordFromFilters(filters: Partial<RequestSponsoredAdsBodyFilters>) {
  //find most specific zc- 'zc1', 'zc2', etc.
  const mostSpecificZcKey = Object.keys(filters).reduce((val, current) => {
    if (current.includes('zc')) {
      //ex: zc2 > zc1
      return current > val ? current : val;
    }

    return val;
  }, '') as keyof RequestSponsoredAdsBodyFilters;

  //all of these are optional on the filters object, if they exist add it to the keyword.
  let keyword = filters.txAttrFacet_Gender?.[0] || '';
  keyword = concatIfExists(keyword, filters[mostSpecificZcKey]?.[0]);
  keyword = concatIfExists(keyword, filters.brandNameFacet?.[0]);
  keyword = concatIfExists(keyword, filters.colorFacet?.[0]);
  return keyword;
}

//configure the payload for the get request to sponsoredAds endpoint
//handle the response and return the results.
export function fetchSponsoredAds(
  args: RequestSponsoredAdsBody,
  sponsoredAdsUrl: string,
  controller: AbortController
): Promise<SponsoredAdsResponse> {
  const body = {
    ...args
  };

  return getAmazonSponsoredAds({ body, endpointUrl: sponsoredAdsUrl, controller }).then((data: AmazonAdsResponse) => {
    const { results, impressionUrl } = data;

    return {
      AMAZON: {
        results,
        impressionUrl
      }
    };
  });
}

type UseSponsoredAds = Pick<RequestSponsoredAdsBody, 'pageType'> & Partial<Pick<RequestSponsoredAdsBody, 'keywords'>>;

//usage in PDP
type UseSponsoredAdsWithStockId = UseSponsoredAds & Required<Pick<RequestSponsoredAdsBody, 'stockId' | 'keywords'>>;

//usage in Search
type UseSponsoredAdsWithFilters = UseSponsoredAds &
  Required<Pick<RequestSponsoredAdsBody, 'filters'>> &
  Pick<RequestSponsoredAdsBody, 'placements' | 'term'> & { matchStyles?: string[] };

function isUseSponsoredAdsWithFilters(args: UseSponsoredAdsWithStockId | UseSponsoredAdsWithFilters): args is UseSponsoredAdsWithFilters {
  return (args as UseSponsoredAdsWithFilters).filters !== undefined;
}

export const useSponsoredAds = (
  args: UseSponsoredAdsWithStockId | UseSponsoredAdsWithFilters,
  isMarketingCurated = false,
  fetchAds = fetchSponsoredAds
) => {
  const [sponsoredAds, setSponsoredAds] = useState<SponsoredAdsResults>();

  const sessionId = useSelector((state: AppState) => state.cookies?.['session-id']);
  const { title: titleTag, canonical: url } = useSelector((state: AppState) => state.meta?.documentMeta) ?? {}; //documentMeta can be null
  const {
    marketplace: {
      search: { hasSponsoredAds },
      is6pm
    }
  } = useMartyContext();
  const sponsoredAdsUrl = useSelector((state: AppState) => selectSponsoredAdsConfig(state).url);
  const { width = 0 } = useWindowSize(200);
  const clientType = getViewportAsString(width);
  const placements = AD_PLACEMENT_COUNTS[clientType === 'mobile' ? 'reduced' : 'full'];

  // require all pieces of information before fetching sponsoredAds.
  useEffect(() => {
    // early return if no marketplace support or missing required pieces from state that are async changes via other actions.
    if (!hasSponsoredAds || isMarketingCurated || !sessionId || !args.pageType || !titleTag || !url || !clientType || width === 0 || is6pm) {
      return;
    }

    // we need either or of these so if they dont exist, return;
    if (!(args as UseSponsoredAdsWithStockId).stockId && !(args as UseSponsoredAdsWithFilters).filters) {
      return;
    }

    const payload = {
      sessionId,
      titleTag,
      url,
      placements,
      clientType,
      ...args
    } as RequestSponsoredAdsBody;

    if (isUseSponsoredAdsWithFilters(args)) {
      payload.keywords = [buildKeywordFromFilters(args.filters)];
    }

    const controller = new AbortController();
    fetchAds(payload, sponsoredAdsUrl, controller)
      .then(data => {
        setSponsoredAds(data.AMAZON);
      })
      .catch(() => {});

    return () => {
      controller.abort('component unmounted during fetch');
    };
  }, [sessionId, titleTag, url, JSON.stringify(args), clientType, width]);

  return sponsoredAds;
};

export const trackSponsoredAdsEvents = ({ url, fetcherName }: { url: string; fetcherName: string }, fetcher = timedFetch) => {
  const fetch = fetcher(fetcherName);
  fetch(url).catch((error: any) => trackError('ERROR', 'Failed to send Amazon Ads event', error));
};
